import React, { useState, Fragment } from "react";
import "./TriggerCard.scss";
import { Menu, MenuItem } from "@mui/material";
import {
  CodeIcon,
  EditIcon,
  PauseIcon,
  PlayIcon,
  DuplicateIcon,
  ArrowForwardIcon,
  DeactivateIcon,
  CheckIcon,
  ProvideFeedbackIcon,
} from "../icons";
import {
  PulseButton,
  PulseButtonSizes,
  PulseButtonTheme,
  PulsePopupContainerSize,
  PulseSwitch,
  PulseSwitchSize,
} from "@pulselabs-ai/shared-fe-ui";
import CustomDialog from "../customDialog/CustomDialog";
import { PulsePopupConfirmation } from "../PulsePopupConfirmation/PulsePopupConfirmation";
import FeedbackPopover from "../FeedbackPopover/FeedbackPopover";
import TriggerInfoPopover from "../TriggerInfoPopover/TriggerInfoPopover";
import MfaChangeFeedbackFormModal from "../dialogs/MfaChangeFeedbackFormModal/MfaChangeFeedbackFormModal";
import TriggerButton from "../TriggerButton/TriggerButton";
// Import or define the survey schema types
interface SurveyChoice {
  text: string;
  value: string;
}

interface SurveyElement {
  name: string;
  type: string;
  title: string;
  choices?: SurveyChoice[];
  isRequired?: boolean;
  colCount?: number;
  otherText?: string;
  showOtherItem?: boolean;
}

interface SurveyPage {
  name: string;
  elements: SurveyElement[];
}

interface SurveySchema {
  pages: SurveyPage[];
  title: string;
  description: string;
}

interface TriggerCardProps {
  title: string;
  status: string;
  triggerType: string;
  triggerId: string;
  projectId: string;
  currentSurveyId: string;
  rowCount: number | string;
  menuVisible?: boolean;
  onViewCode?: () => void;
  onEdit?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onDuplicate?: () => void;
  onDeactivate?: () => void;
  onTriggerChanged: () => void;
  launchCustomAction?: () => void;
  onActivate?: (active: boolean) => void | Promise<void>;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  style?: React.CSSProperties;
  surveySchema: SurveySchema; // Updated to use proper type instead of any
}

const TriggerCard: React.FC<TriggerCardProps> = ({
  title,
  status,
  triggerType,
  triggerId,
  projectId,
  currentSurveyId,
  rowCount,
  menuVisible = false,
  onEdit,
  onPause,
  onResume,
  onDuplicate,
  onDeactivate,
  launchCustomAction,
  onActivate,
  onTriggerChanged,
  selectable = false,
  selected = false,
  onSelect,
  style,
  surveySchema,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showDocsPopup, setShowDocsPopup] = useState(false);
  const [showSetupPopup, setShowSetupPopup] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [feedbackAnchorEl, setFeedbackAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [triggerAnchorEl, setTriggerAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [showMfaChangeFeedbackFormModal, setShowMfaChangeFeedbackFormModal] =
    useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewCode = () => {
    console.log("Opening docs popup");
    setShowDocsPopup(true);
  };

  const handleSetup = () => {
    console.log("Opening setup popup");
    setShowSetupPopup(true);
  };

  const VIEW_CODE = {
    label: "View Code",
    icon: <CodeIcon />,
    onClick: handleViewCode,
  };
  const EDIT = {
    label: "Edit",
    icon: <EditIcon />,
    onClick: onEdit ?? (() => {}),
  };
  const PAUSE = {
    label: "Pause",
    icon: <PauseIcon />,
    onClick: onPause ?? (() => {}),
  };
  const DUPLICATE = {
    label: "Duplicate",
    icon: <DuplicateIcon />,
    onClick: onDuplicate ?? (() => {}),
  };
  const DEACTIVATE = {
    label: "Deactivate",
    icon: <DeactivateIcon />,
    onClick: () => setShowDeactivateConfirm(true),
  };
  const RESUME = {
    label: "Resume",
    icon: <PlayIcon />,
    onClick: onResume ?? (() => {}),
  };

  const getMenuItems = (status: string, triggerType: string) => {
    if (
      triggerType === "Custom" &&
      (status === "LAUNCHED" || status === "PAUSED")
    ) {
      status += "_CUSTOM";
    }
    console.log(triggerType, status);

    switch (status) {
      case "DRAFT":
        return [VIEW_CODE, EDIT, DUPLICATE, DEACTIVATE];
      case "LAUNCHED":
        return [EDIT, PAUSE];
      case "LAUNCHED_CUSTOM":
        return [VIEW_CODE, EDIT, PAUSE, DUPLICATE, DEACTIVATE];
      case "PAUSED":
        return [EDIT, RESUME];
      case "PAUSED_CUSTOM":
        return [VIEW_CODE, EDIT, RESUME, DUPLICATE, DEACTIVATE];
      case "CLOSED":
        return [DUPLICATE];
      default:
        return [];
    }
  };

  const menuItems = getMenuItems(status, triggerType);

  const getDisplayStatus = (status: string) => {
    switch (status) {
      case "ACTIVE":
      case "LAUNCHED":
        return "IN PROGRESS";
      case "INACTIVE":
      case "CLOSED":
        return "DEACTIVATED";
      default:
        return status;
    }
  };

  const handleFeedbackOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFeedbackAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setFeedbackAnchorEl(null);
  };

  const isFeedbackOpen = Boolean(feedbackAnchorEl);

  const handleTriggerOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setTriggerAnchorEl(event.currentTarget);
  };

  const handleCloseTriggerPopover = () => {
    setTriggerAnchorEl(null);
  };

  const handleChangeFeedbackForm = () => {
    setShowMfaChangeFeedbackFormModal(true);
  };

  const isTriggerInfoOpen = Boolean(triggerAnchorEl);

  const handleTriggerChanged = () => {
    onTriggerChanged();
  };

  return (
    <Fragment>
      <div
        className={`trigger-card ${selected ? "selected" : ""}`}
        onClick={() => selectable && onSelect?.()}
        style={{ cursor: selectable ? "pointer" : "default", ...style }}
      >
        <div className="card-header">
          <span
            className={`status ${status === "INACTIVE" ? "status-inactive" : ""}`}
          >
            {getDisplayStatus(status)}
          </span>
          <PulseSwitch
            checked={status === "ACTIVE"}
            onChange={(e) => onActivate?.(e.target.checked)}
            size={PulseSwitchSize.SMALL}
          />
          {menuVisible && (
            <button className="more-options" onClick={handleMenuOpen}>
              ⋮
            </button>
          )}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            {menuItems.map((item, index) => (
              <MenuItem
                key={index}
                onClick={() => {
                  item.onClick();
                  handleMenuClose();
                }}
                className="menu-item"
              >
                {item.icon}
                <span>{item.label}</span>
              </MenuItem>
            ))}
          </Menu>
        </div>
        <div className="title-row">
          <div
            className={`sub-headline-xs ${status === "INACTIVE" ? "text-inactive" : ""}`}
          >
            {title}
          </div>
          <div className="count">
            {typeof rowCount === "string" && rowCount === "" ? (
              <span className="row-count"></span>
            ) : (
              <span className="row-count">{rowCount}</span>
            )}
          </div>
        </div>
        <div className="card-footer">
          <div className="trigger-type card-footer-item">
            <TriggerButton
              triggerType={triggerType}
              status={status}
              onClick={handleTriggerOpen}
              onSetupClick={handleSetup}
              disabled={status === "INACTIVE"}
            />
          </div>
          <ArrowForwardIcon
            className={` ${status === "INACTIVE" ? "arrow-icon-inactive" : "arrow-icon"}`}
          />
          <div className="questions-count card-footer-item">
            <PulseButton
              theme={PulseButtonTheme.LINK}
              size={PulseButtonSizes.EXTRA_SMALL}
              startIcon={<ProvideFeedbackIcon />}
              onClick={handleFeedbackOpen}
              disabled={status === "INACTIVE"}
            >
              PROVIDE FEEDBACK
            </PulseButton>
          </div>
        </div>
      </div>

      <FeedbackPopover
        anchorEl={feedbackAnchorEl}
        open={isFeedbackOpen}
        onClose={handleClosePopover}
        onChangeFeedbackForm={handleChangeFeedbackForm}
        surveySchema={surveySchema}
      />

      <TriggerInfoPopover
        anchorEl={triggerAnchorEl}
        open={isTriggerInfoOpen}
        onClose={handleCloseTriggerPopover}
        triggerType={triggerType}
        customOffset={[-20, 36]}
      />

      <CustomDialog
        isOpen={showDocsPopup}
        onClose={() => {
          console.log("Closing docs popup");
          setShowDocsPopup(false);
        }}
        containerSize={PulsePopupContainerSize.SMALL}
        roundedCorners={true}
      >
        <div className="docs-dialog">
          <h2>Create Custom Triggers</h2>
          <div className="docs-links">
            <PulseButton
              onClick={() =>
                window.open(
                  "https://pulselabs.gitbook.io/pulse-flightrecorder-docs/sdk-documentation/pulse-labs-flightrecorder-documentation/ios/create-custom-triggers",
                  "_blank",
                )
              }
            >
              iOS Documentation
            </PulseButton>
            <PulseButton
              onClick={() =>
                window.open(
                  "https://pulselabs.gitbook.io/pulse-flightrecorder-docs/sdk-documentation/pulse-labs-flightrecorder-documentation/android/create-custom-triggers",
                  "_blank",
                )
              }
            >
              Android Documentation
            </PulseButton>
          </div>
        </div>
      </CustomDialog>
      <CustomDialog
        isOpen={showSetupPopup}
        onClose={() => {
          console.log("Closing setup popup");
          setShowSetupPopup(false);
        }}
      >
        <div className="setup-dialog">
          <h2>Configure Custom action</h2>
          <div className="docs-links">
            <PulseButton
              onClick={() =>
                window.open(
                  "https://pulselabs.gitbook.io/pulse-flightrecorder-docs/sdk-documentation/pulse-labs-flightrecorder-documentation/ios/create-custom-triggers",
                  "_blank",
                )
              }
            >
              iOS Documentation
            </PulseButton>
            <PulseButton
              onClick={() =>
                window.open(
                  "https://pulselabs.gitbook.io/pulse-flightrecorder-docs/sdk-documentation/pulse-labs-flightrecorder-documentation/android/create-custom-triggers",
                  "_blank",
                )
              }
            >
              Android Documentation
            </PulseButton>
          </div>
          <div className="configured-button">
            <PulseButton
              startIcon={<CheckIcon />}
              fullWidth
              onClick={() => {
                launchCustomAction?.();
                setShowSetupPopup(false);
              }}
            >
              THIS HAS BEEN CONFIGURED
            </PulseButton>
          </div>
        </div>
      </CustomDialog>
      <PulsePopupConfirmation
        isOpen={showDeactivateConfirm}
        onClose={() => setShowDeactivateConfirm(false)}
        icon={<DeactivateIcon />}
        title="Are you sure you want to deactivate this capture method?"
        description="Feedback will no longer be collected for this capture method, but you will be able to duplicate it to create a new capture method."
        confirmButtonText="DEACTIVATE"
        cancelButtonText="CANCEL"
        onConfirm={onDeactivate ?? (() => {})}
      />

      <MfaChangeFeedbackFormModal
        isOpen={showMfaChangeFeedbackFormModal}
        onClose={() => setShowMfaChangeFeedbackFormModal(false)}
        onChanged={() => {
          handleTriggerChanged();
        }}
        triggerId={triggerId}
        triggerName={title}
        triggerType={triggerType}
        projectId={projectId}
        currentSurveyId={currentSurveyId}
      />
    </Fragment>
  );
};

export default TriggerCard;
